import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Input } from '@angular/core';
import { BroadcastService } from '@services/broadcast-service';

/**
 * Broadcasts a `KEYBOARD_VISIBLE` boolean so consumers can hide whatever the keyboard would cover.
 * To *position* something instead, use `KeyboardInsetDirective` — see the "Keyboard handling" section
 * of `client/app/onboarding/onboarding-profile/CLAUDE.md`.
 */
@Directive({
  selector: '[keyboardVisibility]',
  standalone: false,
})
export class KeyboardVisibilityDirective implements OnInit, OnDestroy {
  @Input() keyboardVisibilityEnabled: boolean = true;
  private activeInputs: Set<HTMLElement> = new Set();
  private initialViewportHeight: number = 0;
  private lastViewportHeight: number = 0;
  private viewportResizeHandler?: () => void;
  private visualViewport?: VisualViewport;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private broadcast: BroadcastService,
  ) {}

  ngOnInit(): void {
    if (!this.keyboardVisibilityEnabled) return;

    // Initialize - check if any input is already focused
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && this.isInputElement(activeElement) && 
        this.elementRef.nativeElement.contains(activeElement)) {
      this.handleFocus(activeElement);
    }

    // Set up viewport resize detection for keyboard dismissal (e.g., Android back button)
    this.setupViewportResizeDetection();
  }

  ngOnDestroy(): void {
    // Clean up
    this.activeInputs.clear();
    this.removeViewportResizeDetection();
  }

  @HostListener('focusin', ['$event.target'])
  onFocusIn(target: HTMLElement): void {
    if (!this.keyboardVisibilityEnabled) return;
    if (this.isInputElement(target)) {
      this.handleFocus(target);
    }
  }

  @HostListener('focusout', ['$event.target'])
  onFocusOut(target: HTMLElement): void {
    if (!this.keyboardVisibilityEnabled) return;
    if (this.isInputElement(target)) {
      // Small delay to handle touch events properly
      requestAnimationFrame(() => {
        this.handleBlur(target);
      });
    }
  }

  private isInputElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    
    // Textarea always triggers keyboard
    if (tagName === 'textarea') {
      return true;
    }
    
    // For input elements, only consider types that trigger the keyboard
    if (tagName === 'input') {
      const inputElement = element as HTMLInputElement;
      const inputType = inputElement.type?.toLowerCase() || 'text';
      
      // Input types that trigger keyboard on mobile
      const keyboardTriggeringTypes = [
        'text', 'email', 'number', 'tel', 'password', 'search', 
        'url', 'date', 'datetime-local', 'month', 'time', 'week'
      ];
      
      // Exclude types that don't trigger keyboard (checkbox, radio, button, etc.)
      return keyboardTriggeringTypes.includes(inputType);
    }
    
    return false;
  }

  private handleFocus(element: HTMLElement): void {
    if (this.activeInputs.size === 0) {
      // First input focused - keyboard is now visible
      this.broadcast.broadcast('KEYBOARD_VISIBLE', true);
    }
    this.activeInputs.add(element);
  }

  private handleBlur(element: HTMLElement): void {
    this.activeInputs.delete(element);
    
    // Check if we should hide keyboard
    // Wait a bit to see if focus moved to another input in the same form
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      const isFocusStillInForm = activeElement && 
        this.elementRef.nativeElement.contains(activeElement) &&
        this.isInputElement(activeElement);
      
      if (!isFocusStillInForm && this.activeInputs.size === 0) {
        // No inputs focused - keyboard is hidden
        this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
      }
    }, 50);
  }

  private setupViewportResizeDetection(): void {
    // Use Visual Viewport API if available (better for mobile keyboard detection)
    if (typeof window !== 'undefined' && window.visualViewport) {
      this.visualViewport = window.visualViewport;
      this.initialViewportHeight = this.visualViewport.height;
      this.lastViewportHeight = this.initialViewportHeight;
      
      this.viewportResizeHandler = () => {
        this.checkViewportForKeyboardDismissal();
      };
      
      this.visualViewport.addEventListener('resize', this.viewportResizeHandler);
    } else {
      // Fallback to window resize for older browsers
      this.initialViewportHeight = window.innerHeight;
      this.lastViewportHeight = this.initialViewportHeight;
      
      this.viewportResizeHandler = () => {
        this.checkViewportForKeyboardDismissal();
      };
      
      window.addEventListener('resize', this.viewportResizeHandler);
    }
  }

  private removeViewportResizeDetection(): void {
    if (this.viewportResizeHandler) {
      if (this.visualViewport) {
        this.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
      } else {
        window.removeEventListener('resize', this.viewportResizeHandler);
      }
      this.viewportResizeHandler = undefined;
    }
  }

  private checkViewportForKeyboardDismissal(): void {
    if (!this.keyboardVisibilityEnabled || this.activeInputs.size === 0) {
      // Update last height even when no active inputs (for tracking)
      const currentViewportHeight = this.visualViewport?.height || window.innerHeight;
      this.lastViewportHeight = currentViewportHeight;
      // Update initial height if viewport is larger (orientation change, etc.)
      if (currentViewportHeight > this.initialViewportHeight) {
        this.initialViewportHeight = currentViewportHeight;
      }
      return;
    }

    const currentViewportHeight = this.visualViewport?.height || window.innerHeight;
    const heightIncrease = currentViewportHeight - this.lastViewportHeight;
    
    // If viewport height increased significantly (keyboard closed via back button)
    // Threshold: viewport increased by at least 100px or is back to ~95% of initial height
    const significantHeightIncrease = heightIncrease > 100;
    const backToInitialHeight = currentViewportHeight >= this.initialViewportHeight * 0.95;
    
    if (significantHeightIncrease || backToInitialHeight) {
      // Check if any active input is still actually focused
      const activeElement = document.activeElement as HTMLElement;
      const hasFocusedInput = activeElement && 
        this.elementRef.nativeElement.contains(activeElement) &&
        this.isInputElement(activeElement) &&
        this.activeInputs.has(activeElement);
      
      if (!hasFocusedInput) {
        // Keyboard was dismissed (e.g., via back button) but input lost focus
        // Clear active inputs and hide keyboard
        this.activeInputs.clear();
        this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
      }
    }
    
    // Update tracking heights
    this.lastViewportHeight = currentViewportHeight;
    // Update initial height if viewport grew (orientation change, etc.)
    if (currentViewportHeight > this.initialViewportHeight) {
      this.initialViewportHeight = currentViewportHeight;
    }
  }
}

